import { useRequiredContext } from "@redotech/react-util/context";
import {
  useTriggerLoad,
  useTriggerLoadWithInput,
} from "@redotech/react-util/load";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoProgressBar } from "@redotech/redo-web/arbiter-components/progress-bar/redo-progress-bar";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useEffect, useMemo, useState } from "react";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";
import { TeamContext } from "../../team";

interface UnlinkProgress {
  status: "running" | "done" | "idle";
  total: number;
  processed: number;
  failed: number;
}

// TODO @joe/organizations - make this a pretty standalone table rather than a list of names
export const ConnectTeamTo3plCard = memo(function ConnectTeamTo3plCard() {
  const client = useRequiredContext(RedoAdminRpcClientContext);
  const team = useRequiredContext(TeamContext);
  const [teamToFulfillFor, setTeamToFulfillFor] = useState<string>("");
  const [teamPendingUnlink, setTeamPendingUnlink] = useState<{
    _id: string;
    name: string;
  } | null>(null);
  const [unlinkingTeamId, setUnlinkingTeamId] = useState<string | null>(null);
  const [unlinkProgress, setUnlinkProgress] = useState<UnlinkProgress | null>(
    null,
  );

  const [fulfillsForTeamsLoad, getFulfillsForTeams] = useTriggerLoad(
    async () => {
      return await client.getFulfillForTeams({
        fulfillmentServiceTeamId: team._id,
      });
    },
  );

  useEffect(() => {
    getFulfillsForTeams();
  }, [getFulfillsForTeams]);

  const [connectTeamTo3plLoad, connectTeamTo3pl] = useTriggerLoad(async () => {
    await client.connectTeamTo3pl({
      fulfillmentServiceTeamId: team._id,
      teamToFulfillFor,
    });
    getFulfillsForTeams();
  });

  const connectedTeams = useMemo(() => {
    return fulfillsForTeamsLoad.value?.teams ?? [];
  }, [fulfillsForTeamsLoad.value]);

  useEffect(() => {
    if (!unlinkingTeamId) {
      return;
    }
    let cancelled = false;
    const abort = new AbortController();
    (async () => {
      while (!cancelled) {
        let sawTerminal = false;
        try {
          const iter = client.watchUnlinkTeamFromFulfillmentServiceProgress(
            { teamId: unlinkingTeamId, fulfillmentServiceTeamId: team._id },
            { signal: abort.signal },
          );
          for await (const update of iter) {
            setUnlinkProgress(update);
            if (update.status !== "running") {
              sawTerminal = true;
              break;
            }
          }
        } catch (err) {
          if (abort.signal.aborted || cancelled) {
            return;
          }
          console.error("Error watching unlink progress, reconnecting", err);
        }
        if (sawTerminal || cancelled || abort.signal.aborted) {
          return;
        }
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
    })();
    return () => {
      cancelled = true;
      abort.abort();
    };
  }, [unlinkingTeamId, client, team._id]);

  useEffect(() => {
    if (unlinkProgress?.status === "done") {
      getFulfillsForTeams();
      setUnlinkingTeamId(null);
    }
  }, [unlinkProgress?.status, getFulfillsForTeams]);

  const [unlinkLoad, startUnlink] = useTriggerLoadWithInput<string, void>(
    async (teamId) => {
      setUnlinkProgress({
        status: "running",
        total: 0,
        processed: 0,
        failed: 0,
      });
      setUnlinkingTeamId(teamId);
      try {
        await client.unlinkTeamFromFulfillmentService({
          teamId,
          fulfillmentServiceTeamId: team._id,
        });
      } catch (error) {
        console.error("Failed to start unlink workflow:", error);
        setUnlinkingTeamId(null);
        setUnlinkProgress(null);
        alert("Failed to start unlink workflow. Please try again.");
      }
    },
  );

  const confirmUnlink = () => {
    if (!teamPendingUnlink) {
      return;
    }
    const id = teamPendingUnlink._id;
    setTeamPendingUnlink(null);
    startUnlink(id);
  };

  const unlinkPercentage =
    unlinkProgress && unlinkProgress.total > 0
      ? Math.round((unlinkProgress.processed / unlinkProgress.total) * 100)
      : 0;

  return (
    <>
      <Card
        subtitle="This fulfillment service will be able to fulfill for the teams below"
        title="Add a team"
      >
        <Flex dir="row">
          <RedoTextInput
            fullWidth
            placeholder="67bdcae4433b1abda5387360"
            setValue={setTeamToFulfillFor}
            value={teamToFulfillFor}
          />
          <RedoButton
            disabled={teamToFulfillFor.length === 0}
            hierarchy="primary"
            onClick={() => {
              connectTeamTo3pl();
            }}
            pending={connectTeamTo3plLoad.pending}
            text="Add team"
            theme="success"
          />
        </Flex>
        <Flex dir="column" gap="xl" w="full">
          <Flex dir="column" gap="sm">
            {connectedTeams.map((connectedTeam) => {
              const isThisRowUnlinking =
                unlinkingTeamId === connectedTeam._id &&
                unlinkProgress?.status === "running";
              return (
                <Flex
                  align="center"
                  dir="row"
                  gap="md"
                  justify="space-between"
                  key={connectedTeam._id}
                >
                  <Text>{connectedTeam.name}</Text>
                  <RedoButton
                    disabled={
                      unlinkLoad.pending ||
                      (unlinkingTeamId !== null &&
                        unlinkingTeamId !== connectedTeam._id)
                    }
                    hierarchy="secondary"
                    onClick={() => setTeamPendingUnlink(connectedTeam)}
                    pending={
                      isThisRowUnlinking ||
                      (unlinkLoad.pending &&
                        unlinkingTeamId === connectedTeam._id)
                    }
                    size="sm"
                    text="Unlink"
                    theme="destructive"
                  />
                </Flex>
              );
            })}
          </Flex>
          {unlinkingTeamId && unlinkProgress?.status === "running" && (
            <Flex dir="column" gap="sm">
              <RedoProgressBar
                label
                labelOrientation="right"
                labelStyle="text"
                percentage={unlinkPercentage}
              />
              <Text fontSize="xs" textAlign="left" textColor="secondary">
                {`Reassigned ${unlinkProgress.processed} of ${unlinkProgress.total} open fulfillment groups${
                  unlinkProgress.failed > 0
                    ? ` (${unlinkProgress.failed} failed)`
                    : ""
                }`}
              </Text>
            </Flex>
          )}
          {unlinkProgress?.status === "done" && (
            <Text fontSize="xs" textAlign="left" textColor="secondary">
              {`Unlink complete. Reassigned ${unlinkProgress.processed - unlinkProgress.failed} of ${unlinkProgress.total} open fulfillment groups${
                unlinkProgress.failed > 0
                  ? ` (${unlinkProgress.failed} failed)`
                  : ""
              }.`}
            </Text>
          )}
        </Flex>
      </Card>
      {teamPendingUnlink && (
        <RedoModal
          isOpen
          modalSize="sm"
          onModalCloseRequested={() => setTeamPendingUnlink(null)}
          primaryButton={{
            onClick: confirmUnlink,
            text: "Unlink team",
            theme: "destructive",
          }}
          secondaryButton={{
            onClick: () => setTeamPendingUnlink(null),
            text: "Cancel",
          }}
          subtitle={`Unlink ${teamPendingUnlink.name} from this fulfillment service?`}
          theme="error"
          title="Unlink team"
        >
          <Flex dir="column" gap="sm">
            <Text fontSize="sm">This will:</Text>
            <Text fontSize="sm">
              • Remove the link between {teamPendingUnlink.name}
            </Text>
            <Text fontSize="sm">
              • Find all open fulfillment groups for {teamPendingUnlink.name}{" "}
              currently assigned to this fulfillment service and reassign them
              back to {teamPendingUnlink.name}.
            </Text>
            <Text fontSize="sm">• Refresh rates on each reassigned group.</Text>
            <Text fontSize="sm" fontWeight="semibold">
              This will NOT remove any automation actions or change any
              already-fulfilled orders.
            </Text>
          </Flex>
        </RedoModal>
      )}
    </>
  );
});
